import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import PageHeader from '../../molecules/PageHeader';
import TextEdit from '../../molecules/TextEdit';
import FileUpload from '../../molecules/FileUpload';
import ActionButton from '../../molecules/ActionButton';
import { uploadInBatches, BatchUploadProgress } from '../../../services/BatchUploadService';
import { STRUCTURING_ROUTES, STRUCTURING_BREADCRUMBS } from './structuringUtils';
import appConfig from '../../../config/app-config';
import {
    FormData,
    UploadState,
    DocumentResult,
    initialUploadState,
    validateForm,
    showValidationError,
    showSuccess,
    showError,
    findOperationByCode,
    fetchOperationPreview,
} from './OperationPageFormBackend';

const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;
const MAX_SEARCH_RESULTS = 10;

const OperationPageForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>({ operationName: '', files: [], assignorName: '' });
    const [uploadState, setUploadState] = useState<UploadState>(initialUploadState);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Cedente search state
    const [searchCedente, setSearchCedente] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedCedenteName, setSelectedCedenteName] = useState('');
    const [selectedCedenteId, setSelectedCedenteId] = useState('');
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const abortController = useRef<AbortController | null>(null);

    const handlePersonSearch = (value: string) => {
        setSearchCedente(value);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        if (abortController.current) abortController.current.abort();

        if (value.length < MIN_SEARCH_LENGTH) { setSearchResults([]); return; }

        debounceTimer.current = setTimeout(async () => {
            setIsSearching(true);
            abortController.current = new AbortController();
            try {
                const token = await Auth.currentSession().then(s => s.getIdToken().getJwtToken());
                const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${value}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY_PERSON || '' },
                    signal: abortController.current.signal,
                });
                if (response.ok) {
                    const data = await response.json();
                    setSearchResults((data.results || []).slice(0, MAX_SEARCH_RESULTS));
                }
            } catch (e: any) {
                if (e.name !== 'AbortError') console.error(e);
            } finally { setIsSearching(false); }
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleSelectCedente = (cedente: any) => {
        const doc = cedente.documents?.find((d: any) => d.type === 'CNPJ') || cedente.documents?.find((d: any) => d.type === 'CPF');
        setSelectedCedenteId(cedente.uuid);
        setSelectedCedenteName(`${doc?.number || ''} - ${cedente.name}`);
        setFormData(prev => ({ ...prev, assignorName: cedente.name }));
        setSearchResults([]);
        setSearchCedente('');
    };

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    useEffect(() => () => stopPolling(), [stopPolling]);

    const startPolling = useCallback((operationId: string, creditOperationsId: string, totalFiles: number) => {
        stopPolling();
        setUploadState(prev => ({
            ...prev,
            isPolling: true,
            operationId,
            creditOperationsId,
            totalFilesUploaded: totalFiles,
            operationStatus: 'processing',
            documentResults: [],
        }));

        pollingRef.current = setInterval(async () => {
            const result = await fetchOperationPreview(creditOperationsId);
            if (!result) return;

            setUploadState(prev => {
                const done = result.status !== 'processing';
                const allDone = result.document_results.length > 0 
                    && result.document_results.length >= totalFiles
                    && result.document_results.every((d: DocumentResult) => d.processing_status !== 'pending');
                if (done || allDone) stopPolling();
                return {
                    ...prev,
                    documentResults: result.document_results,
                    operationStatus: result.status,
                    isPolling: !(done || allDone),
                };
            });
        }, 3000);
    }, [stopPolling]);

    const handleSave = async () => {
        if (!selectedCedenteName) { showValidationError('Selecione um cedente'); return; }
        const validation = validateForm(formData);
        if (!validation.valid) { showValidationError(validation.error!); return; }

        const totalFiles = formData.files.length;

        try {
            setUploadState(prev => ({ ...prev, isProcessing: true, uploadProgress: null, totalFilesUploaded: totalFiles }));

            const result = await uploadInBatches(
                formData.operationName,
                formData.files,
                30,
                (progress: BatchUploadProgress) => {
                    setUploadState(prev => ({
                        ...prev,
                        uploadProgress: progress,
                        processingStatus: `Enviando lote ${progress.currentBatch} de ${progress.totalBatches}...`,
                    }));
                },
                formData.assignorName
            );

            setUploadState(prev => ({
                ...prev,
                isProcessing: false,
                uploadProgress: null,
                operationCode: result.operation_code,
                summary: result.summary,
            }));

            if (result.success) {
                showSuccess('Upload concluído! Iniciando processamento...');
                if (result.operation_id && result.credit_operations_id) {
                    startPolling(result.operation_id, result.credit_operations_id, totalFiles);
                } else if (result.operation_id) {
                    const opId = await findOperationByCode(result.operation_code);
                    if (opId) {
                        startPolling(result.operation_id, '', totalFiles);
                    } else {
                        showError('Não foi possível localizar a operação para acompanhar o processamento.');
                    }
                } else {
                    showError('Não foi possível localizar a operação para acompanhar o processamento.');
                }
            } else {
                showError(`Upload parcialmente concluído. ${result.errors.length} lotes falharam.`);
            }
        } catch (err: any) {
            showError(err.message || 'Erro de conexão com o servidor');
            setUploadState(prev => ({ ...prev, isProcessing: false, uploadProgress: null }));
        }
    };

    const handleReset = () => {
        stopPolling();
        setUploadState(initialUploadState);
        setFormData({ operationName: '', files: [], assignorName: '' });
        setSelectedCedenteName('');
        setSelectedCedenteId('');
    };

    const total = uploadState.totalFilesUploaded;
    const allDocumentsProcessed = uploadState.documentResults.length > 0 
        && uploadState.documentResults.length >= total 
        && uploadState.documentResults.every(d => d.processing_status !== 'pending');
    const pollingDone = allDocumentsProcessed || (!uploadState.isPolling && !!uploadState.operationId && !!uploadState.operationStatus && uploadState.operationStatus !== 'processing');
    const isFormDisabled = uploadState.isProcessing || uploadState.isPolling || pollingDone || uploadState.operationStatus === 'completed' || uploadState.operationStatus === 'failed';
    const successCount = uploadState.documentResults.filter(d => d.processing_status === 'success').length;
    const errorCount = uploadState.documentResults.filter(d => d.processing_status === 'error').length;
    const processed = uploadState.documentResults.filter(d => d.processing_status !== 'pending').length;
    const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;

    const fileResults = isFormDisabled
        ? formData.files.reduce<Record<string, { status: 'success' | 'error' | 'pending'; message?: string }>>((acc, file) => {
            const match = uploadState.documentResults.find(doc => doc && doc.document_title === file.name);
            if (match && match.processing_status === 'success') {
                acc[file.name] = { status: 'success', message: `${match.receivables_count} recebíve${match.receivables_count !== 1 ? 'is' : 'l'}` };
            } else if (match && match.processing_status === 'error') {
                acc[file.name] = { status: 'error', message: match.processing_error || 'Erro no processamento' };
            } else {
                acc[file.name] = { status: 'pending' };
            }
            return acc;
        }, {})
        : undefined;

    return (
        <div className="p-4">
            <PageHeader
                title="Nova operação"
                breadcrumb={[STRUCTURING_BREADCRUMBS.structuring, STRUCTURING_BREADCRUMBS.newOperation]}
                apiVersion=""
            >
                {pollingDone ? (
                    <ActionButton
                        label="Voltar para listagem"
                        className="bg-gray-600 hover:bg-gray-700"
                        onClick={() => navigate(STRUCTURING_ROUTES.list)}
                    />
                ) : !isFormDisabled && (
                    <ActionButton
                        label={uploadState.isProcessing ? 'Enviando...' : 'Salvar'}
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => { if (!uploadState.isProcessing) void handleSave(); }}
                    />
                )}
            </PageHeader>

            {/* Cedente + Nome da operação card */}
            <div className={`mt-4 bg-white dark:bg-slate-800 border border-gray-400 dark:border-gray-500 rounded-lg p-4 ${isFormDisabled ? 'pointer-events-none opacity-50' : ''}`}>
                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cedente Selecionado</label>
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100">
                        {selectedCedenteName || 'Nenhum cedente selecionado'}
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-lg">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                        <div className="w-2/5">
                            <TextEdit label="Buscar Cedente" value={searchCedente} onChange={(e) => handlePersonSearch(e.target.value)} disabled={isFormDisabled} />
                        </div>
                    </div>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 dark:text-gray-100">Documento</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 dark:text-gray-100">Nome</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 dark:text-gray-100">Tipo</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 dark:text-gray-100 w-24">Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isSearching ? (
                                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                                            <span>Buscando cedentes...</span>
                                        </div>
                                    </td></tr>
                                ) : searchResults.length > 0 ? searchResults.map((cedente, i) => {
                                    const cnpj = cedente.documents?.find((d: any) => d.type === 'CNPJ');
                                    const cpf = cedente.documents?.find((d: any) => d.type === 'CPF');
                                    return (
                                        <tr key={cedente.uuid} className={`${i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700/50'} hover:bg-gray-100 dark:hover:bg-slate-600 border-b border-gray-200 dark:border-gray-600`}>
                                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{cnpj?.number || cpf?.number || '-'}</td>
                                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{cedente.name}</td>
                                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{cnpj ? 'CNPJ' : cpf ? 'CPF' : '-'}</td>
                                            <td className="px-4 py-2">
                                                <button onClick={() => handleSelectCedente(cedente)} className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white font-medium rounded text-xs px-3 py-1.5">
                                                    Selecionar
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr><td colSpan={4} className="px-4 py-4 text-center text-gray-400 dark:text-gray-500 text-sm">
                                        {searchCedente.length >= MIN_SEARCH_LENGTH ? 'Nenhum resultado encontrado' : 'Digite ao menos 3 caracteres para buscar'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            <div className="mt-4">
                <TextEdit
                    label="Nome da operação"
                    value={formData.operationName}
                    onChange={(e) => setFormData(prev => ({ ...prev, operationName: e.target.value }))}
                    disabled={isFormDisabled}
                />
            </div>
            </div>

            {/* Upload progress bar */}
            {uploadState.uploadProgress && (
                <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            {uploadState.processingStatus}
                        </span>
                        <span className="text-sm text-blue-600 dark:text-blue-400">
                            {uploadState.uploadProgress.processedFiles}/{uploadState.uploadProgress.totalFiles} arquivos
                        </span>
                    </div>
                    <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                        <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(uploadState.uploadProgress.processedFiles / uploadState.uploadProgress.totalFiles) * 100}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mt-1">
                        <span>Lote {uploadState.uploadProgress.currentBatch}/{uploadState.uploadProgress.totalBatches}</span>
                        <span>{Math.round((uploadState.uploadProgress.processedFiles / uploadState.uploadProgress.totalFiles) * 100)}%</span>
                    </div>
                </div>
            )}

            {/* Processing progress bar */}
            {(uploadState.isPolling || pollingDone) && (
                <div className="mt-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {uploadState.isPolling
                                ? `Processando documentos... (${processed}/${total})`
                                : `Processamento concluído`
                            }
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            {uploadState.operationCode}
                        </span>
                    </div>

                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
                        <div
                            className={`h-3 rounded-full transition-all duration-500 ${pollingDone ? (errorCount > 0 ? 'bg-amber-500' : 'bg-green-500') : 'bg-blue-500'}`}
                            style={{ width: `${pollingDone ? 100 : progressPct}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-3">
                        <span>{processed} de {total} documentos</span>
                        <span>{pollingDone ? '100' : progressPct}%</span>
                    </div>

                    {pollingDone && (
                        <>
                            <div className={`mb-3 p-3 rounded-lg ${errorCount > 0 ? 'bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-700' : 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-700'}`}>
                                <span className={`text-sm font-medium ${errorCount > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>
                                    {successCount} sucesso{successCount !== 1 ? 's' : ''}, {errorCount} erro{errorCount !== 1 ? 's' : ''} de {total} total
                                </span>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => navigate(STRUCTURING_ROUTES.view(uploadState.operationId))}
                                    className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white font-medium rounded text-sm px-5 py-2.5"
                                >
                                    Ver Operação
                                </button>
                                <button
                                    onClick={handleReset}
                                    className="bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-medium rounded text-sm px-5 py-2.5"
                                >
                                    Nova Operação
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* File upload area - always mounted to preserve state */}
            <div className="mt-4">
                <FileUpload
                    accept=".xml"
                    name="files"
                    label="Arquivos"
                    errorMessage=""
                    onUploadFile={(uploadedFile) => setFormData(prev => ({ ...prev, files: [...prev.files, uploadedFile] }))}
                    onRemoveFile={() => setFormData(prev => ({ ...prev, files: [] }))}
                    disabled={isFormDisabled}
                    multiple={true}
                    canRemove={!isFormDisabled}
                    fileResults={fileResults}
                    files={formData.files}
                />
            </div>
        </div>
    );
};

export default OperationPageForm;
